<script setup>
import { ChevronLeftIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  data: {
    type: Object,
    required: true,
    default: () => {},
  },
  liked: {
    type: Boolean,
    default: false,
  },
  watch: {
    type: Boolean,
    default: false,
  },
});

if (props.data?.error) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
    fatal: true,
  });
}

const contentId = props.data.urlId;
</script>

<template>
  <div class="bg-[#000060]">
    <section class="relative h-[calc(100dvh-86px)] w-full">
      <AtomImageNuxtImage
        class="block aspect-square size-full object-cover lg:aspect-video"
        :src="data.images?.detailImage"
        alt="Image"
      />

      <div
        class="absolute left-7 top-4 mr-7 flex max-w-2xl sm:top-6 lg:top-8 2xl:left-20 2xl:mr-20"
      >
        <button
          type="button"
          class="mr-2 rounded-full transition-transform hover:scale-110 active:scale-95 xl:hidden"
          @click="useBackNavigation"
        >
          <ChevronLeftIcon class="w-8 stroke-2 text-white lg:w-10" aria-hidden="true" />
        </button>
        <MoleculePlayerTitleAndActor
          class="flex items-center gap-x-4 rounded-full bg-gradient-to-r from-black/50 to-black/30 text-white"
          :data="data"
        />
      </div>

      <div
        v-if="data.duration"
        class="absolute bottom-16 right-4 z-[1] flex items-center gap-1 rounded-full bg-black/60 px-3 py-2 text-sm leading-6 text-white sm:bottom-20 sm:right-6 lg:hidden"
      >
        <MoleculeNuxtIcon name="duration" class="size-4 text-[#00E6FF]" />
        {{ beautifyDuration(data?.duration) }}
      </div>

      <div
        class="absolute bottom-0 z-30 flex w-full justify-between bg-[#D9D9D9]/60 px-4 py-2 text-lg font-bold uppercase text-white shadow-[10px_-10px_10px_-10px_rgba(0,0,0,0.2)] backdrop-blur-md dark:bg-[#082792]/30 sm:px-6 sm:py-2.5 lg:py-3 lg:text-xl xl:px-20 2xl:text-2xl"
      >
        <div class="flex w-full items-center">
          <div class="mr-2 flex items-center gap-3 lg:gap-6">
            <div
              v-if="
                data.videoType !== 'StFlatVer' &&
                data.videoType !== 'StFlatHor' &&
                data.videoType !== '2D'
              "
              class="flex h-fit items-center justify-center rounded-full bg-white/30 px-5 py-1 shadow-sm"
            >
              <p class="text-sm font-black lg:text-base 2xl:text-2xl">
                {{
                  data.videoType === 'StHalfSpherVer' ||
                  data.videoType === 'StHalfSpherHor' ||
                  data.videoType === '180'
                    ? '180°'
                    : '360°'
                }}
              </p>
            </div>
            <div
              v-if="data.videoType"
              class="flex h-fit items-center justify-center rounded-full bg-white/30 px-5 py-1 shadow-sm"
            >
              <p class="text-sm font-black lg:text-base 2xl:text-2xl">
                {{
                  data.videoType === '2D' || data.videoType === '180' || data.videoType === '360'
                    ? '2D'
                    : '3D'
                }}
              </p>
            </div>
          </div>
          <div class="hidden w-full justify-center gap-6 md:flex">
            <NuxtLink
              v-if="watch"
              :to="{ name: `video-play`, params: { id: contentId } }"
              class="custom-gradient relative flex w-full max-w-36 items-center rounded-full px-2 py-1 transition-transform before:rounded-full hover:scale-110 active:scale-95 lg:max-w-52 2xl:max-w-60"
            >
              <MoleculeNuxtIcon
                name="circle-play"
                class="size-6 shrink-0 fill-[url(#gradient)] lg:size-8 2xl:size-10"
              />
              <span class="mx-auto">PLAY</span>
            </NuxtLink>
            <NuxtLink
              v-else
              :to="{
                name: `video-purchase`,
                params: { id: contentId },
                query: { boughtByContentId: props.data._id },
              }"
              class="w-full max-w-36 lg:max-w-52 2xl:max-w-60"
            >
              <button
                class="custom-gradient relative flex w-full max-w-36 items-center rounded-full px-3 py-1 text-white transition-transform before:rounded-full hover:scale-110 active:scale-95 lg:max-w-52 2xl:max-w-60"
              >
                <MoleculeNuxtIcon
                  name="ticket"
                  class="size-6 fill-[url(#gradient)] lg:size-8 2xl:size-10"
                />
                <span class="mx-auto">TICKETS</span>
              </button>
            </NuxtLink>
            <NuxtLink
              :to="{ name: `video-preview`, params: { id: contentId } }"
              class="custom-gradient relative flex w-full max-w-36 items-center rounded-full px-2 py-1 text-white transition-transform before:rounded-full hover:scale-110 active:scale-95 lg:max-w-52 2xl:max-w-60"
            >
              <MoleculeNuxtIcon
                name="eye"
                class="size-6 fill-[url(#gradient)] lg:size-8 2xl:size-10"
              />
              <span class="mx-auto">PREVIEW</span>
            </NuxtLink>
          </div>
        </div>
        <div class="flex w-fit items-center gap-4">
          <div class="ml-2 flex items-center justify-center gap-x-5">
            <MoleculeActionLike
              :item-id="data._id"
              :liked-by-user="liked"
              content-type="video"
              class="size-10"
            />
            <MoleculeActionShare
              :url="`video/${contentId}`"
              class="[&>div]:-top-40 [&>div]:origin-bottom"
              placement="bottom"
            />
          </div>
          <button
            type="button"
            class="hidden w-fit items-center gap-1 rounded-full px-6 py-2 transition-transform hover:scale-110 active:scale-95 xl:flex"
            @click="useBackNavigation"
          >
            <MoleculeNuxtIcon name="back" class="size-8 fill-white" />
            BACK
          </button>
        </div>
      </div>
    </section>
    <section
      class="prose max-w-none bg-white px-4 py-5 dark:prose-invert lg:prose-xl dark:bg-[#0F2260] sm:px-6 xl:px-20"
    >
      <p v-text="data.html_description" />
      <div class="flex w-full justify-center gap-4 md:hidden">
        <NuxtLink
          v-if="watch"
          :to="{ name: `video-play`, params: { id: contentId } }"
          class="button-border-gradient flex w-full max-w-36 px-2 py-1 no-underline before:rounded-full"
        >
          <MoleculeNuxtIcon name="circle-play" class="size-6 shrink-0 fill-[url(#gradient)]" />
          <span class="mx-auto">PLAY</span>
        </NuxtLink>
        <NuxtLink
          v-else
          :to="{
            name: `video-purchase`,
            params: { id: contentId },
            query: { boughtByContentId: props.data._id },
          }"
          class="w-full max-w-36 no-underline"
        >
          <button class="button-border-gradient flex w-full max-w-36 px-3 py-1 before:rounded-full">
            <MoleculeNuxtIcon name="ticket" class="size-6 fill-[url(#gradient)]" />
            <span class="mx-auto">TICKETS</span>
          </button>
        </NuxtLink>
        <NuxtLink
          :to="{ name: `video-preview`, params: { id: contentId } }"
          class="button-border-gradient flex w-full max-w-36 px-2 py-1 no-underline before:rounded-full"
        >
          <MoleculeNuxtIcon name="eye" class="size-6 fill-[url(#gradient)]" />
          <span class="mx-auto">PREVIEW</span>
        </NuxtLink>
      </div>
    </section>
  </div>
</template>
